import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react';
import {
  NotebookDetail,
  NotebookCell,
  NotebookSession,
  CellOutput,
  getNotebook,
  updateNotebook,
  createCell,
  updateCell,
  deleteCell,
  reorderCells,
  startSession,
  stopSession,
  restartSession,
  executeCell,
} from '@/services/notebooksApi';

// =============================================================================
// Types
// =============================================================================

interface NotebookState {
  // Notebook data
  notebook: NotebookDetail | null;
  isLoading: boolean;
  error: string | null;

  // Session state
  session: NotebookSession | null;
  sessionStatus: 'disconnected' | 'connecting' | 'ready' | 'busy' | 'error';

  // Execution state
  executingCellId: string | null;

  // Editor state
  selectedCellId: string | null;
  focusedCellId: string | null;
  editingCellId: string | null;

  // Dirty tracking
  dirtySourceMap: Record<string, string>;
}

// =============================================================================
// Actions
// =============================================================================

type Action =
  | { type: 'SET_NOTEBOOK'; payload: NotebookDetail | null }
  | { type: 'SET_NOTEBOOK_NAME'; payload: string }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SESSION'; payload: NotebookSession | null }
  | { type: 'SET_SESSION_STATUS'; payload: NotebookState['sessionStatus'] }
  | { type: 'SET_EXECUTING_CELL_ID'; payload: string | null }
  | { type: 'SET_SELECTED_CELL_ID'; payload: string | null }
  | { type: 'SET_FOCUSED_CELL_ID'; payload: string | null }
  | { type: 'SET_EDITING_CELL_ID'; payload: string | null }
  | { type: 'UPDATE_CELL'; payload: NotebookCell }
  | { type: 'ADD_CELL'; payload: NotebookCell }
  | { type: 'REMOVE_CELL'; payload: string }
  | { type: 'REORDER_CELLS'; payload: NotebookCell[] }
  | { type: 'SET_CELL_OUTPUTS'; payload: { cellId: string; outputs: CellOutput[]; executionCount: number; status: string } }
  | { type: 'SET_DIRTY_SOURCE'; payload: { cellId: string; source: string } }
  | { type: 'CLEAR_DIRTY_SOURCE'; payload: string };

const initialState: NotebookState = {
  notebook: null,
  isLoading: false,
  error: null,
  session: null,
  sessionStatus: 'disconnected',
  executingCellId: null,
  selectedCellId: null,
  focusedCellId: null,
  editingCellId: null,
  dirtySourceMap: {},
};

// Map backend session status to frontend session status
function mapSessionStatus(backendStatus: string | undefined): NotebookState['sessionStatus'] {
  if (!backendStatus) return 'disconnected';

  switch (backendStatus) {
    case 'ready':
    case 'idle':
      return 'ready';
    case 'busy':
      return 'busy';
    case 'starting':
      return 'connecting';
    case 'error':
      return 'error';
    case 'stopped':
    default:
      return 'disconnected';
  }
}

function reducer(state: NotebookState, action: Action): NotebookState {
  switch (action.type) {
    case 'SET_NOTEBOOK':
      return {
        ...state,
        notebook: action.payload,
        session: action.payload?.active_session || null,
        sessionStatus: mapSessionStatus(action.payload?.active_session?.status),
      };

    case 'SET_NOTEBOOK_NAME':
      if (!state.notebook) return state;
      return {
        ...state,
        notebook: {
          ...state.notebook,
          name: action.payload,
        },
      };

    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload };

    case 'SET_SESSION':
      return { ...state, session: action.payload };

    case 'SET_SESSION_STATUS':
      return { ...state, sessionStatus: action.payload };

    case 'SET_EXECUTING_CELL_ID':
      return { ...state, executingCellId: action.payload };

    case 'SET_SELECTED_CELL_ID':
      return { ...state, selectedCellId: action.payload };

    case 'SET_FOCUSED_CELL_ID':
      return { ...state, focusedCellId: action.payload };

    case 'SET_EDITING_CELL_ID':
      return { ...state, editingCellId: action.payload };

    case 'UPDATE_CELL':
      if (!state.notebook) return state;
      return {
        ...state,
        notebook: {
          ...state.notebook,
          cells: state.notebook.cells.map((c) =>
            c.id === action.payload.id ? action.payload : c
          ),
        },
      };

    case 'ADD_CELL':
      if (!state.notebook) return state;
      const cells = [...state.notebook.cells, action.payload];
      cells.sort((a, b) => a.position - b.position);
      return {
        ...state,
        notebook: {
          ...state.notebook,
          cells,
          cell_count: cells.length,
        },
      };

    case 'REMOVE_CELL':
      if (!state.notebook) return state;
      const filtered = state.notebook.cells.filter((c) => c.id !== action.payload);
      return {
        ...state,
        notebook: {
          ...state.notebook,
          cells: filtered,
          cell_count: filtered.length,
        },
        selectedCellId:
          state.selectedCellId === action.payload ? null : state.selectedCellId,
      };

    case 'REORDER_CELLS':
      if (!state.notebook) return state;
      return {
        ...state,
        notebook: {
          ...state.notebook,
          cells: action.payload,
        },
      };

    case 'SET_CELL_OUTPUTS':
      if (!state.notebook) return state;
      return {
        ...state,
        notebook: {
          ...state.notebook,
          cells: state.notebook.cells.map((c) =>
            c.id === action.payload.cellId
              ? {
                  ...c,
                  outputs: action.payload.outputs,
                  execution_count: action.payload.executionCount,
                  execution_status: action.payload.status as NotebookCell['execution_status'],
                }
              : c
          ),
        },
      };

    case 'SET_DIRTY_SOURCE':
      return {
        ...state,
        dirtySourceMap: {
          ...state.dirtySourceMap,
          [action.payload.cellId]: action.payload.source,
        },
      };

    case 'CLEAR_DIRTY_SOURCE':
      const { [action.payload]: _, ...rest } = state.dirtySourceMap;
      return { ...state, dirtySourceMap: rest };

    default:
      return state;
  }
}

// =============================================================================
// Context
// =============================================================================

interface NotebookContextValue {
  state: NotebookState;
  // Notebook operations
  loadNotebook: (notebookId: string) => Promise<void>;
  renameNotebook: (name: string) => Promise<void>;
  // Cell operations
  addCell: (cellType?: 'code' | 'markdown', position?: number) => Promise<void>;
  removeCell: (cellId: string) => Promise<void>;
  moveCell: (cellId: string, direction: 'up' | 'down') => Promise<void>;
  updateCellSource: (cellId: string, source: string) => void;
  saveCellSource: (cellId: string, sourceOverride?: string) => Promise<void>;
  // Selection
  selectCell: (cellId: string | null) => void;
  focusCell: (cellId: string | null) => void;
  editCell: (cellId: string | null) => void;
  // Session operations
  startKernel: () => Promise<void>;
  stopKernel: () => Promise<void>;
  restartKernel: () => Promise<void>;
  // Execution
  runCell: (cellId: string) => Promise<void>;
  runAllCells: () => Promise<void>;
  interruptExecution: () => void;
}

const NotebookContext = createContext<NotebookContextValue | null>(null);

// =============================================================================
// Provider
// =============================================================================

interface NotebookProviderProps {
  children: ReactNode;
}

export function NotebookProvider({ children }: NotebookProviderProps) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load notebook data
  const loadNotebook = useCallback(async (notebookId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });

    try {
      const notebook = await getNotebook(notebookId);
      dispatch({ type: 'SET_NOTEBOOK', payload: notebook });

      // Select first cell if exists
      if (notebook.cells.length > 0) {
        dispatch({ type: 'SET_SELECTED_CELL_ID', payload: notebook.cells[0].id });
      }
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to load notebook',
      });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Rename notebook
  const renameNotebook = useCallback(
    async (name: string) => {
      if (!state.notebook) return;

      try {
        await updateNotebook(state.notebook.id, { name });
        dispatch({ type: 'SET_NOTEBOOK_NAME', payload: name });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to rename notebook',
        });
      }
    },
    [state.notebook]
  );

  // Add a new cell
  const addCell = useCallback(
    async (cellType: 'code' | 'markdown' = 'code', position?: number) => {
      if (!state.notebook) return;

      try {
        const cell = await createCell(state.notebook.id, {
          cell_type: cellType,
          source: '',
          position,
        });
        dispatch({ type: 'ADD_CELL', payload: cell });
        dispatch({ type: 'SET_SELECTED_CELL_ID', payload: cell.id });
        dispatch({ type: 'SET_EDITING_CELL_ID', payload: cell.id });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to add cell',
        });
      }
    },
    [state.notebook]
  );

  // Remove a cell
  const removeCell = useCallback(
    async (cellId: string) => {
      if (!state.notebook) return;

      try {
        await deleteCell(state.notebook.id, cellId);
        dispatch({ type: 'REMOVE_CELL', payload: cellId });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to delete cell',
        });
      }
    },
    [state.notebook]
  );

  // Move a cell up or down
  const moveCell = useCallback(
    async (cellId: string, direction: 'up' | 'down') => {
      if (!state.notebook) return;

      const cells = state.notebook.cells;
      const currentIndex = cells.findIndex((c) => c.id === cellId);
      if (currentIndex === -1) return;

      const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      if (newIndex < 0 || newIndex >= cells.length) return;

      // Create new order
      const newCells = [...cells];
      [newCells[currentIndex], newCells[newIndex]] = [newCells[newIndex], newCells[currentIndex]];

      // Get ordered cell IDs
      const cellIds = newCells.map((c) => c.id);

      try {
        const updatedCells = await reorderCells(state.notebook.id, cellIds);
        dispatch({ type: 'REORDER_CELLS', payload: updatedCells });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to reorder cells',
        });
      }
    },
    [state.notebook]
  );

  // Update cell source (local only, doesn't save to server)
  const updateCellSource = useCallback((cellId: string, source: string) => {
    dispatch({ type: 'SET_DIRTY_SOURCE', payload: { cellId, source } });
  }, []);

  // Save cell source to server
  // If sourceOverride is provided, use it directly (for blur handlers where state might be stale)
  const saveCellSource = useCallback(
    async (cellId: string, sourceOverride?: string) => {
      if (!state.notebook) return;
      const source = sourceOverride ?? state.dirtySourceMap[cellId];
      if (source === undefined) return;

      // Find current cell to check if source actually changed
      const cell = state.notebook.cells.find((c) => c.id === cellId);
      if (cell && cell.source === source) {
        // No change, just clear dirty flag
        dispatch({ type: 'CLEAR_DIRTY_SOURCE', payload: cellId });
        return;
      }

      try {
        const updated = await updateCell(state.notebook.id, cellId, { source });
        dispatch({ type: 'UPDATE_CELL', payload: updated });
        dispatch({ type: 'CLEAR_DIRTY_SOURCE', payload: cellId });
      } catch (err) {
        dispatch({
          type: 'SET_ERROR',
          payload: err instanceof Error ? err.message : 'Failed to save cell',
        });
      }
    },
    [state.notebook, state.dirtySourceMap]
  );

  // Selection helpers
  const selectCell = useCallback((cellId: string | null) => {
    dispatch({ type: 'SET_SELECTED_CELL_ID', payload: cellId });
  }, []);

  const focusCell = useCallback((cellId: string | null) => {
    dispatch({ type: 'SET_FOCUSED_CELL_ID', payload: cellId });
  }, []);

  const editCell = useCallback((cellId: string | null) => {
    dispatch({ type: 'SET_EDITING_CELL_ID', payload: cellId });
  }, []);

  // Start kernel session
  const startKernel = useCallback(async () => {
    if (!state.notebook) return;

    dispatch({ type: 'SET_SESSION_STATUS', payload: 'connecting' });

    try {
      const session = await startSession(state.notebook.id);
      dispatch({ type: 'SET_SESSION', payload: session });
      dispatch({
        type: 'SET_SESSION_STATUS',
        payload: mapSessionStatus(session.status),
      });
    } catch (err) {
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'error' });
      dispatch({
        type: 'SET_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to start kernel',
      });
    }
  }, [state.notebook]);

  // Stop kernel session
  const stopKernel = useCallback(async () => {
    if (!state.notebook || !state.session) return;

    try {
      await stopSession(state.notebook.id, state.session.id);
      dispatch({ type: 'SET_SESSION', payload: null });
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'disconnected' });
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to stop kernel',
      });
    }
  }, [state.notebook, state.session]);

  // Restart kernel session
  const restartKernel = useCallback(async () => {
    if (!state.notebook || !state.session) return;

    dispatch({ type: 'SET_SESSION_STATUS', payload: 'connecting' });

    try {
      const session = await restartSession(state.notebook.id, state.session.id);
      dispatch({ type: 'SET_SESSION', payload: session });
      dispatch({
        type: 'SET_SESSION_STATUS',
        payload: mapSessionStatus(session.status),
      });
    } catch (err) {
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'error' });
      dispatch({
        type: 'SET_ERROR',
        payload: err instanceof Error ? err.message : 'Failed to restart kernel',
      });
    }
  }, [state.notebook, state.session]);

  // Run a single cell
  const runCell = useCallback(
    async (cellId: string) => {
      if (!state.notebook) return;

      // Get source (dirty or saved)
      const cell = state.notebook.cells.find((c) => c.id === cellId);
      if (!cell) return;
      if (cell.cell_type !== 'code') return;

      const source = state.dirtySourceMap[cellId] ?? cell.source;

      // Save dirty source first
      if (state.dirtySourceMap[cellId] !== undefined) {
        await saveCellSource(cellId);
      }

      // Start kernel if not running
      if (!state.session || state.sessionStatus === 'disconnected') {
        await startKernel();
      }

      dispatch({ type: 'SET_EXECUTING_CELL_ID', payload: cellId });
      dispatch({ type: 'SET_SESSION_STATUS', payload: 'busy' });

      try {
        const result = await executeCell(state.notebook.id, cellId, source);
        dispatch({
          type: 'SET_CELL_OUTPUTS',
          payload: {
            cellId,
            outputs: result.outputs,
            executionCount: result.execution_count,
            status: result.status,
          },
        });
      } catch (err) {
        dispatch({
          type: 'SET_CELL_OUTPUTS',
          payload: {
            cellId,
            outputs: [
              {
                output_type: 'error',
                ename: 'ExecutionError',
                evalue: err instanceof Error ? err.message : 'Execution failed',
                traceback: [],
              },
            ],
            executionCount: 0,
            status: 'error',
          },
        });
      } finally {
        dispatch({ type: 'SET_EXECUTING_CELL_ID', payload: null });
        dispatch({ type: 'SET_SESSION_STATUS', payload: 'ready' });
      }
    },
    [
      state.notebook,
      state.session,
      state.sessionStatus,
      state.dirtySourceMap,
      saveCellSource,
      startKernel,
    ]
  );

  // Run all cells in order
  const runAllCells = useCallback(async () => {
    if (!state.notebook) return;

    for (const cell of state.notebook.cells) {
      if (cell.cell_type === 'code') {
        await runCell(cell.id);
      }
    }
  }, [state.notebook, runCell]);

  // Interrupt execution (placeholder - would need backend support)
  const interruptExecution = useCallback(() => {
    // TODO: Implement interrupt via session API
    dispatch({ type: 'SET_EXECUTING_CELL_ID', payload: null });
    dispatch({ type: 'SET_SESSION_STATUS', payload: 'ready' });
  }, []);

  const value: NotebookContextValue = {
    state,
    loadNotebook,
    renameNotebook,
    addCell,
    removeCell,
    moveCell,
    updateCellSource,
    saveCellSource,
    selectCell,
    focusCell,
    editCell,
    startKernel,
    stopKernel,
    restartKernel,
    runCell,
    runAllCells,
    interruptExecution,
  };

  return (
    <NotebookContext.Provider value={value}>{children}</NotebookContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useNotebook() {
  const context = useContext(NotebookContext);
  if (!context) {
    throw new Error('useNotebook must be used within a NotebookProvider');
  }
  return context;
}

export default NotebookContext;
